import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

export type SearchableSelectOption = {
  disabled?: boolean;
  label: string;
  value: string | number;
  description?: string;
};

type SearchableSelectProps = {
  label?: string;
  name?: string;
  value: string | number;
  options: SearchableSelectOption[];
  onChange: (value: string | number, option: SearchableSelectOption) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  menuPlacement?: "top" | "bottom";
  searchable?: boolean;
  showSelectedBadge?: boolean;
};

const SelectShell = styled.div`
  position: relative;
  width: 100%;
  --search-select-label: #64748b;
  --search-select-text: #0f172a;
  --search-select-muted: #94a3b8;
  --search-select-border: #dbe3ef;
  --search-select-border-strong: #e2e8f0;
  --search-select-focus-border: #93c5fd;
  --search-select-focus-ring: rgba(59, 130, 246, 0.12);
  --search-select-bg: #ffffff;
  --search-select-hover-bg: #fbfdff;
  --search-select-option-hover: #f8fafc;
  --search-select-selected-bg: #eff6ff;
  --search-select-selected-text: #1d4ed8;
  --search-select-badge-bg: #dbeafe;
  --search-select-shadow: 0 16px 34px rgba(15, 23, 42, 0.14);

  body.dark-only & {
    --search-select-label: #a8b0c7;
    --search-select-text: #f8fafc;
    --search-select-muted: #7f89a6;
    --search-select-border: rgba(106, 113, 133, 0.42);
    --search-select-border-strong: rgba(106, 113, 133, 0.5);
    --search-select-focus-border: #5f7cff;
    --search-select-focus-ring: rgba(95, 124, 255, 0.18);
    --search-select-bg: #10101c;
    --search-select-hover-bg: #171827;
    --search-select-option-hover: #171827;
    --search-select-selected-bg: rgba(95, 124, 255, 0.18);
    --search-select-selected-text: #9db0ff;
    --search-select-badge-bg: rgba(95, 124, 255, 0.2);
    --search-select-shadow: 0 16px 34px rgba(2, 6, 23, 0.36);
  }
`;

const SelectLabel = styled.label`
  display: block;
  margin: 0 0 4px 0;
  color: var(--search-select-label);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
`;

const Control = styled.button<{ $isOpen: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 18px;
  align-items: center;
  width: 100%;
  min-height: 34px;
  padding: 7px 9px 7px 10px;
  border: 1px solid
    ${({ $isOpen }) =>
      $isOpen
        ? "var(--search-select-focus-border)"
        : "var(--search-select-border)"};
  border-radius: 7px;
  color: var(--search-select-text);
  background: var(--search-select-bg);
  box-shadow: ${({ $isOpen }) =>
    $isOpen ? "0 0 0 3px var(--search-select-focus-ring)" : "none"};
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--search-select-focus-border);
    background: var(--search-select-hover-bg);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const SelectedText = styled.span<{ $muted?: boolean }>`
  min-width: 0;
  color: ${({ $muted }) =>
    $muted ? "var(--search-select-muted)" : "var(--search-select-text)"};
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled.svg<{ $isOpen: boolean }>`
  display: block;
  width: 18px;
  height: 18px;
  justify-self: center;
  align-self: center;
  color: var(--search-select-label);
  transform: ${({ $isOpen }) => $isOpen ? "rotate(180deg)" : "rotate(0deg)"};
  transition: transform 0.15s ease;
`;

const Menu = styled.div<{ $placement: "top" | "bottom" }>`
  position: absolute;
  top: ${({ $placement }) => $placement === "bottom" ? "calc(100% + 6px)" : "auto"};
  bottom: ${({ $placement }) => $placement === "top" ? "calc(100% + 6px)" : "auto"};
  left: 0;
  z-index: 20;
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--search-select-border-strong);
  border-radius: 8px;
  background: var(--search-select-bg);
  box-shadow: var(--search-select-shadow);
`;

const SearchInput = styled.input`
  width: calc(100% - 16px);
  min-height: 32px;
  margin: 8px;
  padding: 7px 9px;
  border: 1px solid var(--search-select-border);
  border-radius: 7px;
  color: var(--search-select-text);
  background: var(--search-select-bg);
  font-size: 12px;
  outline: none;

  &:focus {
    border-color: var(--search-select-focus-border);
    box-shadow: 0 0 0 3px var(--search-select-focus-ring);
  }

  &::placeholder {
    color: var(--search-select-muted);
  }
`;

const OptionsList = styled.div`
  max-height: 210px;
  overflow-y: auto;
  padding: 4px;
`;

const OptionButton = styled.button<{ $active: boolean; $selected: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  width: 100%;
  padding: 8px 9px;
  border: 0;
  border-radius: 7px;
  color: ${({ $selected }) =>
    $selected
      ? "var(--search-select-selected-text)"
      : "var(--search-select-text)"};
  background: ${({ $active, $selected }) =>
    $selected
      ? "var(--search-select-selected-bg)"
      : $active
      ? "var(--search-select-option-hover)"
      : "var(--search-select-bg)"};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ $selected }) =>
      $selected
        ? "var(--search-select-selected-bg)"
        : "var(--search-select-option-hover)"};
  }
  &:disabled { opacity: .45; cursor: not-allowed; background: var(--search-select-bg); }
`;

const OptionText = styled.span`
  min-width: 0;
`;

const OptionLabel = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OptionDescription = styled.span`
  display: block;
  margin-top: 2px;
  color: var(--search-select-label);
  font-size: 11px;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedBadge = styled.span`
  align-self: center;
  padding: 2px 6px;
  border-radius: 999px;
  color: var(--search-select-selected-text);
  background: var(--search-select-badge-bg);
  font-size: 10px;
  font-weight: 600;
`;

const EmptyState = styled.div`
  padding: 12px;
  color: var(--search-select-label);
  font-size: 12px;
  text-align: center;
`;

const SearchableSelect = ({
  label,
  name,
  value,
  options,
  onChange,
  placeholder = "Select option",
  disabled = false,
  className,
  menuPlacement = "bottom",
  searchable = true,
  showSelectedBadge = true,
}: SearchableSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const controlRef = useRef<HTMLButtonElement>(null);

  const selectedOption = useMemo(
    () => options.find((option) => String(option.value) === String(value)),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return options;

    return options.filter((option) => {
      const labelMatch = option.label.toLowerCase().includes(search);
      const valueMatch = String(option.value).toLowerCase().includes(search);
      const descriptionMatch = option.description
        ?.toLowerCase()
        .includes(search);

      return labelMatch || valueMatch || descriptionMatch;
    });
  }, [options, query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(0);
    const timeout = window.setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0);
    return () => window.clearTimeout(timeout);
  }, [isOpen]);

  const handleSelect = (option: SearchableSelectOption) => {
    if (option.disabled) return;
    onChange(option.value, option);
    setQuery("");
    setIsOpen(false);
    controlRef.current?.focus({ preventScroll: true });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen && ["ArrowDown", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) =>
        Math.min(current + 1, filteredOptions.length - 1)
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === "Enter" && filteredOptions[activeIndex]) {
      event.preventDefault();
      handleSelect(filteredOptions[activeIndex]);
    }
  };

  return (
    <SelectShell
      ref={rootRef}
      className={className}
      onKeyDown={handleKeyDown}
    >
      {label && <SelectLabel htmlFor={name}>{label}</SelectLabel>}
      <Control
        ref={controlRef}
        id={name}
        type="button"
        $isOpen={isOpen}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <SelectedText $muted={!selectedOption}>
          {selectedOption?.label ?? placeholder}
        </SelectedText>
        <Chevron $isOpen={isOpen} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </Chevron>
      </Control>

      {isOpen && (
        <Menu $placement={menuPlacement}>
          {searchable && <SearchInput
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search..."
          />}
          <OptionsList role="listbox">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const selected = String(option.value) === String(value);

                return (
                  <OptionButton
                    key={option.value}
                    type="button"
                    $active={index === activeIndex}
                    $selected={selected}
                    role="option"
                    aria-selected={selected}
                    disabled={option.disabled}
                    aria-disabled={option.disabled || undefined}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => handleSelect(option)}
                  >
                    <OptionText>
                      <OptionLabel>{option.label}</OptionLabel>
                      {option.description && (
                        <OptionDescription>
                          {option.description}
                        </OptionDescription>
                      )}
                    </OptionText>
                    {selected && showSelectedBadge && <SelectedBadge>Selected</SelectedBadge>}
                  </OptionButton>
                );
              })
            ) : (
              <EmptyState>No options found</EmptyState>
            )}
          </OptionsList>
        </Menu>
      )}
    </SelectShell>
  );
};

export default SearchableSelect;
